/**
 * Status Badge Component
 * Displays execution status with appropriate colors
 */

import type { ExecutionStatus } from '../types'

interface StatusBadgeProps {
  status: ExecutionStatus | string | null
  size?: 'sm' | 'md' | 'lg'
}

const statusConfig: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  INITIATED: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500',
    label: 'Initiated',
  },
  RUNNING: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    dot: 'bg-yellow-500 animate-pulse',
    label: 'Running',
  },
  DELIVERING: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    dot: 'bg-indigo-500 animate-pulse',
    label: 'Delivering',
  },
  COMPLETED: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    dot: 'bg-green-500',
    label: 'Completed',
  },
  FAILED: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    dot: 'bg-red-500',
    label: 'Failed',
  },
  CANCELLED: {
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    dot: 'bg-gray-500',
    label: 'Cancelled',
  },
  PAUSED: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    dot: 'bg-orange-500',
    label: 'Paused',
  },
}

const sizeClasses = {
  sm: 'text-xs px-2 py-0.5',
  md: 'text-sm px-2.5 py-1',
  lg: 'text-base px-3 py-1.5',
}

const dotSizes = {
  sm: 'w-1.5 h-1.5',
  md: 'w-2 h-2',
  lg: 'w-2.5 h-2.5',
}

export function StatusBadge({ status, size = 'md' }: StatusBadgeProps) {
  const config = statusConfig[status || ''] || {
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    dot: 'bg-gray-400',
    label: status || 'Unknown',
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium ${config.bg} ${config.text} ${sizeClasses[size]}`}
    >
      <span className={`rounded-full ${config.dot} ${dotSizes[size]}`} />
      {config.label}
    </span>
  )
}

export default StatusBadge
